import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    AppBar, Toolbar, IconButton, Avatar, Box, Typography, Badge, Tooltip,
    useMediaQuery, useTheme, List, ListItemButton, ListItemText,
    Stack, CircularProgress, TextField,
    Paper, ListItem, ListSubheader, Button, InputAdornment, Container,
    Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import SchoolIcon from '@mui/icons-material/School';
import SearchIcon from '@mui/icons-material/Search';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { flattenMenuData } from '../../utils/flattenMenuData';
import NotificationsPopover from './NotificationsPopover';
import UserMenuPopover from './UserMenuPopover';
import LogoutDialog from './LogoutDialog';
import axiosInstance from '../../api/axiosConfig';
import {
    clearAuthSession,
    getStoredRoles,
    hasAllowedRole,
    splitRoles,
} from '../../utils/authSession';
import { useThemeMode } from '../../theme/ThemeModeContext';
import { useAdminNavigation } from '../navigation/AdminNavigationContext';
import { ADMIN_LAYOUT } from '../../adminThemeTokens';
import { useHeaderTitle } from './HeaderTitleContext';

const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
};

const ACADEMIC_YEAR_PATTERN = /^(\d{4})-(\d{4})$/;

const getSuggestedNextAcademicYear = (currentAcademicYear) => {
    const currentYearMatch = ACADEMIC_YEAR_PATTERN.exec((currentAcademicYear || '').trim());
    if (currentYearMatch) {
        const currentEndYear = Number(currentYearMatch[2]);
        return `${currentEndYear}-${currentEndYear + 1}`;
    }
    const currentYear = new Date().getFullYear();
    return `${currentYear}-${currentYear + 1}`;
};

const resolveCloseAcademicYearError = (error) => {
    const status = error?.response?.status;
    const serverMessage = typeof error?.response?.data === 'string' ? error.response.data : '';

    if (status === 400) {
        return serverMessage || 'Invalid academic year input. Use YYYY-YYYY and confirm next sequential year.';
    }
    if (status === 404) {
        return serverMessage || 'School not found. Refresh and retry.';
    }
    if (status === 409) {
        return serverMessage || 'Academic year transition conflict detected. Refresh and retry.';
    }
    return serverMessage || 'Unable to close the academic year right now. Please try again.';
};

const Header = ({ handleDrawerToggle = null, elevated = false }) => {
    const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
    const [userDetails, setUserDetails] = useState({ firstName: '', lastName: '', role: '', avatarUrl: '' });
    const [notifications, setNotifications] = useState([]);
    const [loadingNotifications, setLoadingNotifications] = useState(true);
    const [academicYear, setAcademicYear] = useState(localStorage.getItem('academicYear') || 'N/A');
    const [loadingAcademicYear, setLoadingAcademicYear] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [closeYearDialogOpen, setCloseYearDialogOpen] = useState(false);
    const [newAcademicYearInput, setNewAcademicYearInput] = useState('');
    const [closingAcademicYear, setClosingAcademicYear] = useState(false);
    const [academicYearFormError, setAcademicYearFormError] = useState('');
    const [operationSnackbar, setOperationSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const desktopSearchInputRef = useRef(null);
    const lastSeenNotificationRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { isDarkMode, toggleThemeMode } = useThemeMode();
    useHeaderTitle();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const schoolId = localStorage.getItem('schoolId') || '';
    const userRoles = useMemo(() => getStoredRoles(), []);
    const { menuData } = useAdminNavigation();
    const flattenedMenuData = useMemo(() => flattenMenuData(menuData || []), [menuData]);
    const isNotificationsOpen = Boolean(notificationsAnchorEl);
    const isPlatformAdmin = useMemo(() => {
        return hasAllowedRole(['SUPER_ADMIN', 'APP_ADMIN', 'SYSTEM_ADMIN'], userRoles);
    }, [userRoles]);

    // Determine visibility based on role
    const showAdminFeatures = useMemo(() => {
        return hasAllowedRole(['SCHOOL_ADMIN', 'ADMIN', 'SUPER_ADMIN'], userRoles);
    }, [userRoles]);

    useEffect(() => {
        const storedFirstName = localStorage.getItem('firstName') || '';
        const storedLastName = localStorage.getItem('lastName') || '';
        const storedRole = getStoredRoles()[0] || '';
        const storedEmployeeId = localStorage.getItem('employeeId') || '';
        const storedUserId = localStorage.getItem('userId') || storedEmployeeId || '';
        const storedAvatarUrl = localStorage.getItem('avatarUrl') || '';
        setUserDetails({ firstName: storedFirstName, lastName: storedLastName, role: storedRole, avatarUrl: storedAvatarUrl });

        const toUiNotifications = (items) => {
            if (!Array.isArray(items)) return [];
            return items.map((item) => ({
                id: item.notificationId ?? item.id,
                type: item.type || 'in_app',
                title: item.title || 'Notification',
                message: item.message || '',
                date: item.createdAt || item.created_at || new Date().toISOString(),
                isRead: Boolean(item.isRead),
            }));
        };

        const showBrowserNotification = (item) => {
            if (typeof window === 'undefined' || !('Notification' in window)) return;
            if (document.hidden !== true) return;
            if (Notification.permission !== 'granted') return;
            try {
                new Notification(item.title || 'Skhool', {
                    body: item.message || 'You have a new notification.',
                });
            } catch (_) {
                // ignore
            }
        };

        const fetchNotifications = () => {
            if (!storedUserId || !schoolId) {
                setLoadingNotifications(false);
                return;
            }
            if (typeof window !== 'undefined' && window.__SKHOOL_PAUSE_NOTIFICATIONS__) {
                return;
            }
            setLoadingNotifications(true);
            axiosInstance
                .get('/api/notifications/user', { params: { userId: storedUserId, schoolId } })
                .then((res) => {
                    const mapped = toUiNotifications(res.data || []);
                    const sorted = [...mapped].sort((a, b) => new Date(b.date) - new Date(a.date));
                    const newest = sorted[0];
                    if (newest) {
                        const previous = lastSeenNotificationRef.current;
                        if (previous && String(previous) !== String(newest.id)) {
                            showBrowserNotification(newest);
                        }
                        lastSeenNotificationRef.current = newest.id;
                    }
                    setNotifications(sorted);
                })
                .catch((err) => console.error('Error fetching notifications:', err))
                .finally(() => setLoadingNotifications(false));
        };

        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission().catch(() => {});
        }

        if (isNotificationsOpen) {
            fetchNotifications();
        }
        return undefined;
    }, [schoolId, isNotificationsOpen]);

    const fetchAcademicYear = useCallback(async () => {
        if (!schoolId || schoolId === '0' || !showAdminFeatures || isPlatformAdmin) {
            setAcademicYear('N/A');
            return;
        }
        setLoadingAcademicYear(true);
        try {
            const response = await axiosInstance.get(`/api/schools/academic-year/${schoolId}`);
            const fetchedYear = response.data || 'N/A';
            setAcademicYear(fetchedYear);
            localStorage.setItem('academicYear', fetchedYear);
        } catch (error) {
            console.error('Error fetching academic year:', error);
             setAcademicYear(localStorage.getItem('academicYear') || 'N/A');
        } finally {
            setLoadingAcademicYear(false);
        }
    }, [schoolId, showAdminFeatures, isPlatformAdmin]);

    useEffect(() => { fetchAcademicYear(); }, [fetchAcademicYear]);

    useEffect(() => {
        const type = localStorage.getItem('userType');
        const code = localStorage.getItem('code');
        const schoolHeader = schoolId || localStorage.getItem('schoolId');
        if (!type || !code) return undefined;

        const checkSession = async () => {
            try {
                await axiosInstance.get('/auth/session-status', {
                    params: { type, code },
                    headers: schoolHeader ? { schoolId: schoolHeader } : {},
                    skipGlobalError: true,
                    skipAuthRedirect: true,
                });
            } catch (err) {
                const reason = err?.response?.data?.reason;
                if (reason === 'USER_DISABLED' || err?.response?.status === 403) {
                    clearAuthSession();
                    navigate('/login');
                }
            }
        };

        checkSession();
        const intervalId = setInterval(checkSession, 60000);
        return () => clearInterval(intervalId);
    }, [navigate, schoolId]);

    const getSearchableData = useMemo(() => {
        if (!showAdminFeatures) return []; // Don't generate if not admin
        const menuItems = flattenedMenuData.map((item) => ({ label: item.label, path: item.path, category: 'Menu', hasNotifications: item.hasNotifications || false }));
        const customSearchablePaths = [ { label: 'Add Student', path: '/student/add', category: 'Actions' }, { label: 'Add Employee', path: '/teacher/add', category: 'Actions' }, { label: 'Add Fee Type', path: '/fee-types/create', category: 'Actions' }, { label: 'Add School', path: '/register-school', category: 'Actions' }, { label: 'Add Examination Type', path: '/examination-types/new', category: 'Actions' }, { label: 'Add Class', path: '/classes', category: 'Actions' }, { label: 'Add Subject', path: '/subjects', category: 'Actions' }, { label: 'Add Timetable', path: '/timetable', category: 'Actions' }, { label: 'Add Holiday', path: '/hdays', category: 'Actions' }, { label: 'Add Bus', path: '/transportation/add-route', category: 'Actions' }, { label: 'Add Event', path: '/events', category: 'Actions' }, { label: 'School Setup', path: '/ai-setup', category: 'Actions' }, { label: 'Document Automation', path: '/document-automation', category: 'Actions' }, { label: 'Generate Hall Tickets', path: '/hall-tickets', category: 'Actions' } ];
        const relatedNames = [ { label: 'Student Analytics', path: '/students/analytics', category: 'Related' }, { label: 'Student Records', path: '/students/list', category: 'Related' }, { label: 'Employee Payroll', path: '/payroll-dashboard', category: 'Related' }, { label: 'Fee Management', path: '/fee-management', category: 'Related' }, { label: 'Expense Dashboard', path: '/expenses', category: 'Related' }, { label: 'Attendance Records', path: '/attendance', category: 'Related' }, { label: 'Exam Results', path: '/academic-results', category: 'Related' }, { label: 'School Settings', path: '/school-settings', category: 'Related' }, { label: 'Leave Balance', path: '/leave-balance', category: 'Related' }, { label: 'Library Management', path: '/library', category: 'Related' }, { label: 'Transportation Overview', path: '/transportation/buses', category: 'Related' }, { label: 'Event List', path: '/events', category: 'Related' }, { label: 'Download Reports', path: '/reports', category: 'Related' }, { label: 'Document Automation', path: '/document-automation', category: 'Related' }, { label: 'Document Templates', path: '/document-templates', category: 'Related' }, { label: 'Hall Tickets', path: '/hall-tickets', category: 'Related' } ];
        return [...menuItems, ...customSearchablePaths, ...relatedNames].map(item => ({ ...item, hasNotifications: item.hasNotifications || false }));
    }, [flattenedMenuData, showAdminFeatures]); // Add showAdminFeatures dependency

    const performSearch = useCallback((query) => {
        setIsSearching(true);
        setSearchResults([]);
        if (query.trim() === '') { setIsSearching(false); return; }
        const searchableData = getSearchableData;
        const filteredResults = searchableData.filter((item) => item.label.toLowerCase().includes(query.toLowerCase()));
        setTimeout(() => { setSearchResults(filteredResults); setIsSearching(false); }, 150);
    }, [getSearchableData]);

    const debouncedSearch = useMemo(() => debounce(performSearch, 250), [performSearch]);
    const handleSearchChange = (event) => { const query = event.target.value; setSearchQuery(query); debouncedSearch(query); };
    const handleSearchOverlayClose = useCallback(() => {
        setIsSearchFocused(false);
        setSearchQuery('');
        setSearchResults([]);
        setIsSearching(false);
    }, []);
    const handleSearchSelect = (path) => {
        handleSearchOverlayClose();
        navigate(path);
    };

    const handleLogout = useCallback(() => {
        clearAuthSession();
        navigate('/login');
    }, [navigate]);
    const handleNotificationsOpen = (event) => {
        setNotificationsAnchorEl(event.currentTarget);
    };
    const handleNotificationsClose = () => setNotificationsAnchorEl(null);
    const handleUserMenuOpen = (event) => setUserMenuAnchorEl(event.currentTarget);
    const handleUserMenuClose = () => setUserMenuAnchorEl(null);
    const handleAcademicYearClick = useCallback(() => {
        setAcademicYearFormError('');
        setNewAcademicYearInput(getSuggestedNextAcademicYear(academicYear));
        setCloseYearDialogOpen(true);
    }, [academicYear]);

    const handleCloseYearDialog = useCallback(() => {
        if (!closingAcademicYear) {
            setCloseYearDialogOpen(false);
            setAcademicYearFormError('');
        }
    }, [closingAcademicYear]);

    const handleConfirmCloseAcademicYear = useCallback(async () => {
        if (!schoolId) {
            setOperationSnackbar({
                open: true,
                message: 'School context is missing. Please login again.',
                severity: 'error',
            });
            return;
        }

        const normalizedNewYear = newAcademicYearInput.trim();
        if (!ACADEMIC_YEAR_PATTERN.test(normalizedNewYear)) {
            setAcademicYearFormError('Use YYYY-YYYY format (example: 2026-2027).');
            return;
        }

        setClosingAcademicYear(true);
        setAcademicYearFormError('');
        try {
            await axiosInstance.put(
                `/api/schools/${schoolId}/academic-year/close?newAcademicYear=${encodeURIComponent(normalizedNewYear)}`
            );

            setAcademicYear(normalizedNewYear);
            localStorage.setItem('academicYear', normalizedNewYear);
            window.dispatchEvent(
                new CustomEvent('skhool:academic-year-updated', {
                    detail: { schoolId, academicYear: normalizedNewYear },
                })
            );

            setCloseYearDialogOpen(false);
            setOperationSnackbar({
                open: true,
                message: `Academic year closed. Active year is now ${normalizedNewYear}.`,
                severity: 'success',
            });
        } catch (error) {
            const message = resolveCloseAcademicYearError(error);
            setAcademicYearFormError(message);
            setOperationSnackbar({
                open: true,
                message,
                severity: 'error',
            });
        } finally {
            setClosingAcademicYear(false);
        }
    }, [newAcademicYearInput, schoolId]);

    const isUserMenuOpen = Boolean(userMenuAnchorEl);
    const showSearchResults = isSearchFocused;
    const handleDesktopSearchOpen = useCallback(() => {
        setIsSearchFocused(true);
        window.setTimeout(() => {
            desktopSearchInputRef.current?.focus();
        }, 0);
    }, []);

    return (
        <>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    bgcolor: 'transparent',
                    color: theme.palette.text.primary,
                    backdropFilter: 'blur(18px)',
                    boxShadow: elevated
                        ? `0 10px 24px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.22 : 0.08)}`
                        : 'none',
                    borderBottom: elevated
                        ? `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.34 : 0.7)}`
                        : 'none',
                    top: 0,
                    zIndex: theme.zIndex.appBar,
                    transition: 'box-shadow 180ms ease, border-color 180ms ease',
                }}
            >
                <Toolbar sx={{ minHeight: { xs: 64, md: 0 }, px: 0, py: 0, display: { xs: 'flex', md: 'none' } }}>
                    <Container
                        disableGutters
                        sx={{
                            maxWidth: '100% !important',
                            width: '100%',
                            mx: 0,
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: { xs: 64, md: 0 },
                            border: 'none',
                            borderRadius: 0,
                            background: 'transparent',
                            boxShadow: 'none',
                            pl: 0,
                            pr: ADMIN_LAYOUT.pagePadding,
                            py: { xs: 0.15, md: 0.2 },
                        }}
                    >
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 0.95, md: 1.65 } }}>
                        {isMobile && handleDrawerToggle && (
                            <Tooltip title="Open Menu">
                                <IconButton onClick={handleDrawerToggle} edge="start" sx={{ color: 'text.secondary', border: 'none', borderRadius: 10, width: 34, height: 34, bgcolor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.16 : 0.04), '&:hover': { bgcolor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.24 : 0.08) } }}>
                                    <MenuIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    {showAdminFeatures ? (
                        <Box
                            sx={{
                                flexGrow: 1,
                                maxWidth: { xs: 190, sm: 280, md: 360 },
                                ml: 0,
                                mr: { xs: 0.6, md: 1.4 },
                                display: { xs: 'block', md: 'none' },
                            }}
                        >
                            <Box
                                onClick={handleDesktopSearchOpen}
                                sx={{
                                    minHeight: 40,
                                    borderRadius: 6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1.4,
                                    gap: 1,
                                    cursor: 'text',
                                    bgcolor: theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.common.white, 0.08)
                                      : alpha(theme.palette.background.default, 0.95),
                                    boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.5 : 0.8)}`,
                                }}
                            >
                                <SearchIcon sx={{ color: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.7 : 0.55) }} />
                                <Typography sx={{ color: alpha(theme.palette.text.primary, 0.55), fontSize: '0.85rem' }}>
                                    Search pages and actions
                                </Typography>
                            </Box>
                        </Box>
                    ) : <Box sx={{ flexGrow: 1 }} />}

                    <Stack direction="row" spacing={{ xs: 0.6, md: 1 }} alignItems="center" sx={{ ml: 'auto' }}>
                        <Tooltip title="User Menu">
                            <Box onClick={handleUserMenuOpen} sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', cursor: 'pointer', p: 0.55, borderRadius: 6, border: 'none', bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08), '&:hover': { bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.14) } }}>
                                <Avatar alt={`${userDetails.firstName} ${userDetails.lastName}`} src={userDetails.avatarUrl || '/static/images/avatar/default-avatar.png'} sx={{ width: 32, height: 32 }}>
                                    {userDetails.firstName ? userDetails.firstName[0].toUpperCase() : ''}
                                </Avatar>
                            </Box>
                        </Tooltip>
                    </Stack>
                    </Container>
                </Toolbar>
                <Toolbar sx={{ minHeight: { xs: 0, md: 60 }, px: 0, py: 0, display: { xs: 'none', md: 'flex' } }}>
                    <Container
                        disableGutters
                        sx={{
                            maxWidth: '100% !important',
                            width: '100%',
                            mx: 0,
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: { xs: 0, md: 60 },
                            border: 'none',
                            borderRadius: 0,
                            background: 'transparent',
                            boxShadow: 'none',
                            pl: 0,
                            pr: ADMIN_LAYOUT.pagePadding,
                            py: { xs: 0, md: 0.15 },
                        }}
                    >
                        <Box sx={{ flexGrow: 1 }} />
                        {showAdminFeatures ? (
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ flexShrink: 0, display: { xs: 'none', md: 'flex' } }}
                            >
                                <Box
                                    sx={{
                                        width: 'auto',
                                    }}
                                >
                                    <Tooltip title="Search">
                                        <IconButton
                                            onClick={handleDesktopSearchOpen}
                                            sx={{
                                                color: 'text.secondary',
                                                border: 'none',
                                                borderRadius: 6,
                                                width: 38,
                                                height: 38,
                                                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
                                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.14) },
                                            }}
                                        >
                                            <SearchIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <Tooltip title="User Menu">
                                    <Box onClick={handleUserMenuOpen} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', p: 0.55, borderRadius: 6, border: 'none', bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.1), '&:hover': { bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.16) } }}>
                                        <Avatar alt={`${userDetails.firstName} ${userDetails.lastName}`} src={userDetails.avatarUrl || '/static/images/avatar/default-avatar.png'} sx={{ width: 32, height: 32 }}>
                                            {userDetails.firstName ? userDetails.firstName[0].toUpperCase() : ''}
                                        </Avatar>
                                    </Box>
                                </Tooltip>
                            </Stack>
                        ) : null}
                    </Container>
                </Toolbar>
            </AppBar>

            <Dialog
                open={showSearchResults}
                onClose={handleSearchOverlayClose}
                fullWidth
                maxWidth="md"
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 4,
                        minHeight: isMobile ? '100%' : 520,
                        mt: isMobile ? 0 : 6,
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1.2, pt: 2.2 }}>
                    Search
                </DialogTitle>
                <DialogContent sx={{ pt: '0 !important', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <TextField
                        inputRef={desktopSearchInputRef}
                        variant="outlined"
                        placeholder="Search pages, actions, reports, settings..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onFocus={() => setIsSearchFocused(true)}
                        fullWidth
                        autoFocus
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                minHeight: { xs: 58, md: 64 },
                                fontSize: { xs: '1rem', md: '1.05rem' },
                                bgcolor: theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.common.white, 0.06)
                                  : alpha(theme.palette.background.default, 0.95),
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                              <InputAdornment position="start" sx={{ ml: 0.35 }}>
                                <SearchIcon sx={{ color: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.7 : 0.55) }} />
                              </InputAdornment>
                            ),
                            endAdornment: isSearching ? (
                                <InputAdornment position="end">
                                    <CircularProgress color="inherit" size={18} />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <Paper
                        elevation={0}
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            borderRadius: 1.5,
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Box sx={{ p: 1.25 }}>
                            {!isSearching && searchQuery.trim() === '' && searchResults.length === 0 && (
                                <Typography sx={{ p: 2.5, color: 'text.secondary', textAlign: 'center' }}>
                                    Start typing to search pages and actions
                                </Typography>
                            )}
                            {isSearching && searchResults.length === 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                    <CircularProgress size={26} />
                                </Box>
                            )}
                            {!isSearching && searchQuery.trim() !== '' && searchResults.length === 0 && (
                                <Typography sx={{ p: 2.5, color: 'text.secondary', textAlign: 'center' }}>
                                    No results found for "{searchQuery}"
                                </Typography>
                            )}
                            {searchResults.length > 0 && (
                                <List disablePadding>
                                    {['Menu', 'Actions', 'Related'].map((category) => {
                                        const categoryResults = searchResults.filter((res) => res.category === category);
                                        if (categoryResults.length > 0) {
                                            return (
                                                <React.Fragment key={category}>
                                                    <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '34px', mb: 0.4, mt: category !== 'Menu' ? 1 : 0 }}>
                                                        {category}
                                                    </ListSubheader>
                                                    {categoryResults.map((result) => (
                                                        <ListItem key={result.label + result.path} disablePadding sx={{ borderRadius: 2, overflow: 'hidden', mb: 0.35 }}>
                                                            <ListItemButton onClick={() => handleSearchSelect(result.path)} sx={{ py: 1.1, px: 1.5, borderRadius: 2, '&:hover': { bgcolor: 'action.hover' } }}>
                                                                <ListItemText primary={result.label} primaryTypographyProps={{ color: 'text.primary', fontWeight: 500, fontSize: '0.96rem' }} />
                                                                {result.hasNotifications && <Badge color="error" variant="dot" sx={{ ml: 1 }} />}
                                                            </ListItemButton>
                                                        </ListItem>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        } return null;
                                    })}
                                </List>
                            )}
                        </Box>
                    </Paper>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button onClick={handleSearchOverlayClose}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={closeYearDialogOpen}
                onClose={handleCloseYearDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Close Academic Year</DialogTitle>
                <DialogContent sx={{ pt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Current year: <strong>{academicYear || 'N/A'}</strong>
                    </Typography>
                    <TextField
                        fullWidth
                        label="New Academic Year"
                        placeholder="YYYY-YYYY"
                        value={newAcademicYearInput}
                        onChange={(event) => setNewAcademicYearInput(event.target.value)}
                        error={Boolean(academicYearFormError)}
                        helperText={academicYearFormError || 'Example: 2026-2027'}
                        size="small"
                        autoFocus
                    />
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1.5 }}>
                    <Button onClick={handleCloseYearDialog} disabled={closingAcademicYear}>
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setCloseYearDialogOpen(false);
                            navigate('/school-settings');
                        }}
                        disabled={closingAcademicYear}
                    >
                        Open Settings
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmCloseAcademicYear}
                        disabled={closingAcademicYear}
                        startIcon={closingAcademicYear ? <CircularProgress size={16} color="inherit" /> : <SchoolIcon fontSize="small" />}
                    >
                        {closingAcademicYear ? 'Updating...' : 'Close & Start New'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={operationSnackbar.open}
                autoHideDuration={4200}
                onClose={() => setOperationSnackbar((previous) => ({ ...previous, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={operationSnackbar.severity}
                    variant="filled"
                    onClose={() => setOperationSnackbar((previous) => ({ ...previous, open: false }))}
                    sx={{ width: '100%' }}
                >
                    {operationSnackbar.message}
                </Alert>
            </Snackbar>

            <NotificationsPopover anchorEl={notificationsAnchorEl} isOpen={isNotificationsOpen} onClose={handleNotificationsClose} notifications={notifications} loadingNotifications={loadingNotifications} />
            <UserMenuPopover anchorEl={userMenuAnchorEl} isOpen={isUserMenuOpen} onClose={handleUserMenuClose} userDetails={userDetails} academicYear={academicYear} notificationCount={notifications.length} onThemeToggle={toggleThemeMode} isDarkMode={isDarkMode} onNotificationsOpen={(event) => { handleUserMenuClose(); handleNotificationsOpen({ currentTarget: userMenuAnchorEl || event?.currentTarget }); }} onAcademicYearClick={() => { handleUserMenuClose(); handleAcademicYearClick(); }} onLogoutClick={() => { setLogoutDialogOpen(true); handleUserMenuClose(); }} />
            <LogoutDialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)} onLogout={handleLogout} />
        </>
    );
};

Header.propTypes = {
    handleDrawerToggle: PropTypes.func,
    elevated: PropTypes.bool,
};

export default Header;
